/**
 * MVP block registry — the first 8 blocks ported from the legacy `M` registry
 * (the "Blank" template's core set: logo → hero → headline → body → cta →
 * footer, plus eyebrow + image band). Render output is byte-compatible with
 * the monolith for identical data. The remaining ~31 blocks/modules are
 * inventoried in ../../MIGRATION.md with their legacy line references.
 */
import { AR, esc, btnHTML, theme, inset, escAttr } from "./core";
import { LEN, socDefaults, socRow } from "./brand";
import type { BlockDefinition, BlockDataMap, BlockType } from "../types/blocks";

type Registry = { [T in BlockType]: BlockDefinition<T> };

export const REGISTRY: Registry = {
  logo: {
    label: "Logo header",
    kind: "block",
    grp: "Structure",
    def: () => ({ bg: "white", contact: "", phone: "" }),
    render: (d) => {
      const navy = d.bg === "navy";
      const sub = (d.contact || "").trim();
      const ph = (d.phone || "").trim();
      const has = sub || ph;
      const subCol = navy ? "#c9d4e6" : "#5a6472";
      const phCol = navy ? "#ffffff" : "#1B365D";
      let line = "";
      if (has) {
        const bits: string[] = [];
        if (sub) bits.push('<span style="color:' + subCol + '">' + esc(sub) + "</span>");
        if (ph)
          bits.push(
            '<a href="tel:' +
              esc(ph.replace(/[^0-9+]/g, "")) +
              '" style="color:' +
              phCol +
              ';text-decoration:underline">' +
              esc(ph) +
              "</a>",
          );
        line =
          '<div style="font-family:' +
          AR +
          ';font-size:13px;line-height:18px;padding-top:8px;text-align:center">' +
          bits.join('<span style="color:' + subCol + '">&nbsp;|&nbsp;</span>') +
          "</div>";
      }
      return (
        '<tr><td align="center" style="padding:20px 20px ' +
        (has ? "12px" : "20px") +
        ";text-align:center;background:" +
        (navy ? "#1B2A58" : "#ffffff") +
        ";border-bottom:" +
        (has ? "0" : "1px solid #eeeeee") +
        '"><img src="' +
        (navy ? LEN.logoW : LEN.logo) +
        '" alt="Lennar" width="169" style="display:block;width:169px;max-width:60%;height:auto;margin:0 auto">' +
        line +
        "</td></tr>"
      );
    },
    fields: [
      { k: "bg", label: "Background", type: "seg", opts: [["white", "White"], ["navy", "Navy"]] },
      { k: "contact", label: "Subtext / tagline (optional)", type: "text" },
      { k: "phone", label: "Phone number (optional)", type: "text" },
    ],
  },

  hero: {
    label: "Hero image",
    kind: "block",
    grp: "Content blocks",
    /* legacy def verbatim (monolith M.hero.def) — default hero photo + overlay-baker
       state; __inset is NOT in the def (the monolith's inset select is an
       inspector-level control inside the image card, defaulting to "full") */
    def: () => ({
      src: "https://www.emailimagecdnq1vr.com/iyklq5vx7w/en_us/images/696071b98d599-1767928249.579.jpg",
      alt: "Hero",
      ovOn: "0", ovKicker: "", ovHead: "", ovSub: "", ovCol: "#ffffff",
      ovAlign: "left", ovV: "bottom", ovSize: "40", ovScrim: "35", ovFrame: "0",
    }),
    // Hero img spec mirrors Content Builder Dynamic_Hero_Image: 600 wide, auto
    // height, max-width 600, no border/padding. The legacy overlay editor
    // (bakes kicker/headline/scrim/effect into src via canvas) stays in the
    // monolith — the render contract is unchanged: a single email-safe <img>.
    render: (d) => {
      const I = inset(d.__inset);
      return (
        '<tr><td style="font-size:0;line-height:0;padding:' +
        I.pad +
        '">' +
        (d.src
          ? '<img src="' +
            escAttr(d.src) +
            '" width="' +
            I.w +
            '" alt="' +
            escAttr(d.alt) +
            '" style="display:block;height:auto;max-width:' +
            I.w +
            'px;width:100%;border:0">'
          : '<div style="height:220px;background:#33507d"></div>') +
        "</td></tr>"
      );
    },
    fields: [
      { k: "src", label: "Image URL", type: "image" },
      { k: "alt", label: "Alt text", type: "text" },
      { k: "ovEdit", label: "Overlay", type: "ovedit" },
    ],
  },

  eyebrow: {
    label: "Eyebrow",
    kind: "block",
    grp: "Content blocks",
    def: () => ({ text: "COMING SOON", color: "gold" }),
    render: (d) => {
      const c = ({ gold: "#a9843f", navy: "#1B2A58", teal: "#3f8a8a" } as Record<string, string>)[d.color] || "#a9843f";
      return (
        '<tr><td align="center" style="padding:22px 40px 0;font-family:' +
        AR +
        ";font-size:12px;font-weight:bold;letter-spacing:.18em;text-transform:uppercase;color:" +
        c +
        '">' +
        esc(d.text) +
        "</td></tr>"
      );
    },
    fields: [
      { k: "text", label: "Text", type: "text" },
      { k: "color", label: "Color", type: "seg", opts: [["gold", "Gold"], ["navy", "Navy"], ["teal", "Teal"]] },
    ],
  },

  headline: {
    label: "Headline",
    kind: "block",
    grp: "Content blocks",
    def: () => ({ text: "Rent doesn't have to be forever — owning a home is closer than you think." }),
    render: (d) =>
      '<tr><td align="center" style="padding:24px 50px 8px;font-family:' +
      AR +
      ';font-size:22px;font-weight:bold;line-height:29px;color:#303030">' +
      esc(d.text) +
      "</td></tr>",
    fields: [{ k: "text", label: "Headline", type: "textarea" }],
  },

  body: {
    label: "Body copy",
    kind: "block",
    grp: "Content blocks",
    /* __theme is deliberately NOT in the def or fields (legacy parity): the theme
       select is an inspector-level control (__themeControlHtml, default "white") */
    def: () => ({ text: "Watch our on-demand homebuyer webinar where our professionals break down what it really takes to move from renting to owning." }),
    render: (d) => {
      const th = d.__theme || "white";
      if (th === "white")
        return (
          '<tr><td align="center" style="padding:6px 50px;font-family:' +
          AR +
          ';font-size:14px;line-height:21px;color:#303030">' +
          esc(d.text) +
          "</td></tr>"
        );
      const T = theme(th);
      return (
        '<tr><td align="center" style="padding:22px 50px;font-family:' +
        AR +
        ";font-size:14px;line-height:21px;color:" +
        T.bandTx +
        ";background:" +
        T.band +
        '">' +
        esc(d.text) +
        "</td></tr>"
      );
    },
    fields: [{ k: "text", label: "Paragraph", type: "textarea" }],
  },

  cta: {
    label: "Button / CTA",
    kind: "block",
    grp: "Content blocks",
    def: () => ({
      buttons: "one" as const,
      label: "Watch Now",
      url: "https://buy.lennar.com/webinar/ondemand/",
      label2: "Contact Us",
      url2: "https://www.lennar.com/market?nextPage=contact_rfi",
      style: "filled" as const,
    }),
    render: (d) => {
      const th = d.__theme || "white";
      const two = d.buttons === "two" && (String(d.label2 || "").trim() || String(d.url2 || "").trim());
      if (th === "white") {
        const b1 = btnHTML(d.label, d.url, d.style);
        if (!two) return '<tr><td align="center" style="padding:10px 50px 22px">' + b1 + "</td></tr>";
        const b2 = btnHTML(d.label2 || "", d.url2 || "", d.style);
        return (
          '<tr><td align="center" style="padding:10px 30px 22px"><table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center"><tr><td style="padding:4px 6px">' +
          b1 +
          '</td><td style="padding:4px 6px">' +
          b2 +
          "</td></tr></table></td></tr>"
        );
      }
      const T = theme(th);
      const wb = (lb?: string, u?: string) =>
        '<table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:6px 6px;border-collapse:separate;line-height:100%;display:inline-table"><tr><td align="center" bgcolor="#ffffff" valign="middle" style="border:2px solid #ffffff;border-radius:32px;background:#ffffff;mso-padding-alt:14px 34px"><a href="' +
        escAttr(u || "#") +
        '" target="_blank" style="display:inline-block;font-family:' +
        AR +
        ";font-size:14px;font-weight:bold;color:" +
        T.band +
        ';line-height:120%;padding:14px 34px;mso-padding-alt:0;text-decoration:none;border-radius:32px">' +
        esc(lb || "Button") +
        "</a></td></tr></table>";
      return (
        '<tr><td align="center" style="padding:18px 40px;background:' +
        T.band +
        '">' +
        wb(d.label, d.url) +
        (two ? wb(d.label2, d.url2) : "") +
        "</td></tr>"
      );
    },
    fields: [
      { k: "buttons", label: "Buttons", type: "seg", opts: [["one", "One button"], ["two", "Two buttons"]] },
      { k: "label", label: "Button 1 label", type: "text" },
      { k: "url", label: "Button 1 URL", type: "url" },
      { k: "label2", label: "Button 2 label", type: "text" },
      { k: "url2", label: "Button 2 URL", type: "url" },
      { k: "style", label: "Style", type: "seg", opts: [["filled", "Filled"], ["outline", "Outline"]] },
    ],
  },

  image: {
    label: "Image band",
    kind: "block",
    grp: "Content blocks",
    def: () => ({ src: "https://www.emailimagecdnq1vr.com/iyklq5vx7w/en_us/images/69d53afc0ce6a-1775581948.0528.jpg", alt: "Image", pad: "none" }),
    render: (d) => {
      const p = d.pad === "inset" ? "0 50px 30px" : "0";
      return (
        '<tr><td style="padding:' +
        p +
        ';font-size:0;line-height:0">' +
        (d.src
          ? '<img src="' + escAttr(d.src) + '" width="600" alt="' + escAttr(d.alt) + '" style="display:block;width:100%;height:auto">'
          : '<div style="height:160px;background:#dfe6ef"></div>') +
        "</td></tr>"
      );
    },
    fields: [
      { k: "src", label: "Image URL", type: "image" },
      { k: "alt", label: "Alt text", type: "text" },
      { k: "pad", label: "Width", type: "seg", opts: [["none", "Full-bleed"], ["inset", "Inset"]] },
    ],
  },

  footer: {
    label: "Social footer",
    kind: "block",
    grp: "Structure",
    def: () => ({
      note: "Features, amenities, floor plans, elevations, and designs vary and are subject to changes or substitution without notice. This is not an offer in states where prior registration is required. Void where prohibited. Copyright © Lennar Corporation. Lennar and the Lennar logo are U.S. registered service marks.",
      ...socDefaults(),
    }),
    // In production, Dynamic mode fills the division's REAL social links via
    // /sfmc-social-links; empty link = icon dropped (see socRow).
    render: (d) =>
      '<tr><td align="center" style="padding:28px 25px 20px;background:#1B2A58"><img src="' +
      LEN.flogo +
      '" alt="Lennar Mortgage — Equal Housing Opportunity / Lender" width="200" style="display:block;margin:0 auto 14px;width:200px;max-width:78%;height:auto"><div>' +
      socRow(d) +
      '</div></td></tr><tr><td align="center" style="padding:22px 25px;background:#ffffff;border-top:1px solid #eeeeee"><p style="margin:0 0 10px;font-family:' +
      AR +
      ';font-size:12px;line-height:16px;color:#303030"><strong>To Unsubscribe from future emails <a href="https://www.lennar.com/contact/communicationpreferences" style="color:#0000ee">click here</a>.</strong></p><p style="margin:0;font-family:' +
      AR +
      ';font-size:12px;line-height:16px;color:#303030"><a href="https://www.lennar.com/privacypolicy" style="color:#0000ee">Privacy Policy</a> &nbsp;|&nbsp; <a href="https://www.lennar.com/contact/communicationpreferences" style="color:#0000ee">Manage Communication Preferences</a> &nbsp;|&nbsp; <a href="https://www.lennar.com/termsandconditions" style="color:#0000ee">Terms &amp; Conditions</a> &nbsp;|&nbsp; <a href="https://www.lennar.com/legal" style="color:#0000ee">Disclaimers</a></p>' +
      (d.note
        ? '<p style="margin:14px 0 0;font-family:' +
          AR +
          ';font-size:10.5px;line-height:15px;color:#8a8578;text-align:left">' +
          esc(d.note) +
          "</p>"
        : "") +
      "</td></tr>",
    fields: [
      { k: "note", label: "Legal / disclaimer", type: "textarea" },
      { k: "_soc", label: "Social links — tap an icon to toggle or edit", type: "socgrid" },
    ],
  },
};

export const BLOCK_TYPES = Object.keys(REGISTRY) as BlockType[];

export function defaultData<T extends BlockType>(type: T): BlockDataMap[T] {
  return REGISTRY[type].def();
}
